import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { addDocument, getDocuments, updateDocument, deleteDocument } from '../services/database';
import { buscarProductoEnCatalogo } from '../services/catalogo';
import { buscarMedicamentoPorCodigo } from '../services/medicamentos';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Card, CardHeader, CardBody } from '../components/common/Card';
import { Modal } from '../components/common/Modal';
import { ZXingScanner } from '../components/common/ZXingScanner';
import { AutocompleteInput } from '../components/common/AutocompleteInput';
import { Barcode, Building2, Package, Calendar, Edit3, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency, formatInputNumber, parseFormattedNumber } from '../utils/formatters';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 22px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(217, 119, 6, 0.09) 100%);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(245, 158, 11, 0.18);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(245, 158, 11, 0.22) 0%, transparent 65%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #B45309 0%, #F59E0B 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 14px;
    padding: 16px 14px;
    align-items: stretch;
    border-radius: 16px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
      text-align: center;
    }
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.md};
    
    input {
      font-size: ${props => props.theme.typography.fontSize.sm};
    }
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
    padding: 0 4px;
  }
`;

const ProductCard = styled.div`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdfe 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  padding: 18px;
  overflow: hidden;
  isolation: isolate;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 20px -8px rgba(46, 91, 136, 0.1);
  transition:
    transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 260ms ease,
    border-color 260ms ease;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2E5B88 0%, #4FB3BF 50%, #2E5B88 100%);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 170px;
    height: 170px;
    background: radial-gradient(circle at top right, rgba(79, 179, 191, 0.09) 0%, transparent 60%);
    pointer-events: none;
    z-index: 0;
  }

  & > * {
    position: relative;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(79, 179, 191, 0.4);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.95) inset,
      0 1px 3px rgba(15, 23, 42, 0.05),
      0 16px 36px -12px rgba(46, 91, 136, 0.2),
      0 4px 10px -2px rgba(79, 179, 191, 0.12);
  }

  &.expiring-soon {
    border-color: rgba(245, 158, 11, 0.45);
    &::before {
      background: linear-gradient(90deg, #F59E0B 0%, #D97706 50%, #F59E0B 100%);
    }
    &::after {
      background: radial-gradient(circle at top right, rgba(245, 158, 11, 0.14) 0%, transparent 60%);
    }
  }

  &.expired {
    border-color: rgba(239, 68, 68, 0.5);
    &::before {
      background: linear-gradient(90deg, #EF4444 0%, #B91C1C 50%, #EF4444 100%);
    }
    &::after {
      background: radial-gradient(circle at top right, rgba(239, 68, 68, 0.14) 0%, transparent 60%);
    }
  }

  h3 {
    margin: 10px 0 0;
    padding: 0 0 14px;
    font-size: 16px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.3px;
    line-height: 1.3;
    border-bottom: 1px dashed rgba(148, 163, 184, 0.3);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px;
    border-radius: 14px;

    &::before { height: 3px; }

    h3 {
      font-size: 13px;
      margin: 8px 0 0;
      padding: 0 0 10px;
      line-height: 1.25;
    }
  }
`;

const ProductInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: 14px 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 6px;
    margin: 10px 0;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(46, 91, 136, 0.1) 0%, rgba(79, 179, 191, 0.12) 100%);
    color: #2E5B88;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 14px;
      height: 14px;
      stroke-width: 2.3;
    }
  }

  .content {
    flex: 1;
    min-width: 0;
  }

  label {
    display: block;
    font-size: 9.5px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 1px;
  }

  p {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 6px;

    .icon {
      width: 22px;
      height: 22px;
      border-radius: 6px;

      svg { width: 11px; height: 11px; }
    }

    label { font-size: 8.5px; }
    p { font-size: 11px; }
  }
`;

const StockBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: ${props => props.$low
    ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.14), rgba(245, 158, 11, 0.22))'
    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.14), rgba(16, 185, 129, 0.22))'};
  color: ${props => props.$low ? '#B45309' : '#047857'};
  border: 1px solid ${props => props.$low ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.3)'};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 9.5px;
    padding: 1px 6px;
  }
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  margin: 14px 0 12px;
  background: linear-gradient(135deg, rgba(46, 91, 136, 0.04) 0%, rgba(79, 179, 191, 0.07) 100%);
  border: 1px solid rgba(79, 179, 191, 0.15);
  border-radius: 12px;

  .cost {
    label {
      display: block;
      font-size: 9.5px;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
    }

    .value {
      font-size: 13px;
      font-weight: 600;
      color: #64748b;
      font-variant-numeric: tabular-nums;
    }
  }

  .sale {
    text-align: right;

    label {
      display: block;
      font-size: 9.5px;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 2px;
    }

    .value {
      font-size: 18px;
      font-weight: 800;
      letter-spacing: -0.5px;
      background: linear-gradient(135deg, #059669 0%, #10B981 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-variant-numeric: tabular-nums;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 8px 10px;
    margin: 10px 0 8px;
    border-radius: 10px;

    .cost .value { font-size: 11px; }
    .sale .value { font-size: 14px; }
    label { font-size: 8.5px !important; }
  }
`;

const ExpirationBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 4px 10px;
  background: ${props => props.expired
    ? 'linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)'
    : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'};
  color: #ffffff;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.25) inset,
    0 2px 6px ${props => props.expired ? 'rgba(239, 68, 68, 0.35)' : 'rgba(245, 158, 11, 0.35)'};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 8.5px;
    padding: 2px 6px;
    top: 6px;
    right: 6px;
  }
`;

const AddOptionsDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 14px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 10px 30px -8px rgba(15, 23, 42, 0.2),
    0 2px 6px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  z-index: 1000;
  animation: dropdownIn 180ms cubic-bezier(0.22, 1, 0.36, 1);

  @keyframes dropdownIn {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const AddOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 220px;
  padding: 12px 14px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  color: #1e293b;
  transition: background 160ms ease, color 160ms ease;

  &:hover {
    background: linear-gradient(90deg, rgba(79, 179, 191, 0.1), transparent);
    color: #2E5B88;
  }

  svg {
    width: 20px;
    height: 20px;
    color: #4FB3BF;
    flex-shrink: 0;
  }

  span {
    font-size: 14px;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 200px;
    padding: 10px 12px;

    svg { width: 18px; height: 18px; }
    span { font-size: 13px; }
  }
`;

const HeaderActions = styled.div`
  position: relative;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    button {
      font-size: ${props => props.theme.typography.fontSize.sm};
      padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    }
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${props => props.theme.spacing.lg};
  background: rgba(255, 255, 255, 0.98);
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.md};
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-top: ${props => props.theme.spacing.md};
    padding: ${props => props.theme.spacing.sm};
    margin-left: -${props => props.theme.spacing.sm};
    margin-right: -${props => props.theme.spacing.sm};
    border-radius: 0;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 4px 14px -4px rgba(46, 91, 136, 0.08);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
    table-layout: fixed;
  }
`;

const Th = styled.th`
  padding: 14px 12px;
  background: linear-gradient(135deg, #2E5B88 0%, #4FB3BF 100%);
  color: #ffffff;
  text-align: left;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 6px 4px;
    font-size: 9px;
    letter-spacing: 0.2px;
    white-space: normal;
    line-height: 1.2;
  }
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  background: white;
  
  &.expiring {
    color: ${props => props.theme.colors.warning};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
  }
  
  &.expired {
    color: ${props => props.theme.colors.error};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 3px;
    font-size: 9px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 0;
  }
`;

const Tr = styled.tr`
  background: white;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-top: 0 !important;
    gap: 2px;
    
    button {
      font-size: 8px !important;
      padding: 2px 4px !important;
      flex: 1;
      min-height: 20px;
    }
  }
`;

const FilterContainer = styled.div`
  position: relative;
  display: flex;
  gap: 14px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  align-items: flex-end;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  padding: 18px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 4px 14px -6px rgba(46, 91, 136, 0.08);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 10px;
    padding: 14px;
    margin-bottom: 12px;
    border-radius: 14px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 100%;
  }

  label {
    font-size: 11px;
    color: #64748b;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  select {
    padding: 10px 38px 10px 14px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    border-radius: 10px;
    background-color: #ffffff;
    font-size: 14px;
    color: #1e293b;
    font-weight: 500;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%232E5B88' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 10px center;
    background-size: 16px;
    cursor: pointer;
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &:focus {
      outline: none;
      border-color: #4FB3BF;
      box-shadow: 0 0 0 3px rgba(79, 179, 191, 0.18);
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      padding: 8px 32px 8px 10px;
      font-size: 13px;
    }
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 14px;
  margin-bottom: 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 14px;
  }
`;

const StatCard = styled.div`
  position: relative;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 18px -8px rgba(46, 91, 136, 0.12);
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 220ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.$accent || 'linear-gradient(180deg, #2E5B88, #4FB3BF)'};
  }

  &::after {
    content: '';
    position: absolute;
    top: -30%;
    right: -15%;
    width: 140px;
    height: 140px;
    background: ${props => props.$glow || 'radial-gradient(circle, rgba(46,91,136,0.08), transparent 65%)'};
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.9) inset,
      0 1px 3px rgba(15, 23, 42, 0.06),
      0 12px 28px -10px rgba(46, 91, 136, 0.18);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 14px;
    border-radius: 14px;

    &::before { width: 3px; }
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  font-weight: 700;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 9.5px;
    margin-bottom: 4px;
    letter-spacing: 0.4px;
  }
`;

const StatValue = styled.div`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -1px;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #1e293b, #475569)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
  font-variant-numeric: tabular-nums;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 18px;
    letter-spacing: -0.5px;
    margin-bottom: 2px;
  }
`;

const StatSubtext = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 9.5px;
    line-height: 1.25;
  }
`;

// Catálogo local más completo con estructura similar a la base de datos
const predefinedProducts = [
  { 
    code: '7702123006817', 
    name: 'ASPIRINA 100 MG 140 TBS',
    laboratory: 'BAYER S.A.',
    description: 'ACET.SALICILICO ACIDO',
    presentation: 'CJ',
    cost: 81342,
    salePrice: 81342
  },
  { 
    code: '7501008494004', 
    name: 'Paracetamol 500mg', 
    laboratory: 'Genomma Lab',
    description: 'PARACETAMOL',
    cost: 15000,
    salePrice: 20000
  },
  { 
    code: '7501008473207', 
    name: 'Ibuprofeno 400mg', 
    laboratory: 'Genomma Lab',
    description: 'IBUPROFENO',
    cost: 18000,
    salePrice: 25000
  },
  { 
    code: '7502216793859', 
    name: 'Amoxicilina 500mg', 
    laboratory: 'Pfizer',
    description: 'AMOXICILINA',
    cost: 25000,
    salePrice: 35000
  },
  { 
    code: '7501318612372', 
    name: 'Omeprazol 20mg', 
    laboratory: 'Pisa',
    description: 'OMEPRAZOL',
    cost: 22000,
    salePrice: 30000
  },
  { 
    code: '7501573900153', 
    name: 'Metformina 850mg', 
    laboratory: 'Merck',
    description: 'METFORMINA',
    cost: 20000,
    salePrice: 28000
  },
  { 
    code: '7501318632264', 
    name: 'Losartan 50mg', 
    laboratory: 'Pisa',
    description: 'LOSARTAN',
    cost: 30000,
    salePrice: 40000
  },
  { 
    code: '7501008490365', 
    name: 'Diclofenaco 100mg', 
    laboratory: 'Genomma Lab',
    description: 'DICLOFENACO',
    cost: 15000,
    salePrice: 22000
  },
  { 
    code: '7502216914384', 
    name: 'Ciprofloxacino 500mg', 
    laboratory: 'Pfizer',
    description: 'CIPROFLOXACINO',
    cost: 45000,
    salePrice: 60000
  },
  { 
    code: '7501573902324', 
    name: 'Atorvastatina 20mg', 
    laboratory: 'Merck',
    description: 'ATORVASTATINA',
    cost: 35000,
    salePrice: 48000
  },
  { 
    code: '7702010000000', 
    name: 'Advil 200mg', 
    laboratory: 'Pfizer',
    description: 'IBUPROFENO',
    cost: 12000,
    salePrice: 18000
  },
  { 
    code: '7702057000000', 
    name: 'Dolex 500mg', 
    laboratory: 'GlaxoSmithKline',
    description: 'PARACETAMOL',
    cost: 8000,
    salePrice: 12000
  },
  { 
    code: '7702605100019', 
    name: 'Ensure Vainilla', 
    laboratory: 'Abbott',
    description: 'SUPLEMENTO NUTRICIONAL',
    cost: 55000,
    salePrice: 70000
  }
];

export const Inventory = () => {
  const { userId } = useAuth();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [existingProductFound, setExistingProductFound] = useState(null);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [isProcessingBarcode, setIsProcessingBarcode] = useState(false);
  
  const [formData, setFormData] = useState({
    barcode: '',
    name: '',
    laboratory: '',
    description: '',
    invoiceNumber: '',
    entryDate: format(new Date(), 'yyyy-MM-dd'),
    expirationDate: '',
    cost: '',
    salePrice: '',
    stock: '1'
  });

  useEffect(() => {
    loadProducts();
  }, [userId]);
  
  
  // NO ejecutar automáticamente para evitar confusión

  useEffect(() => {
    // Si viene de la tarjeta de inventario, mostrar vista de tabla
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('view') === 'list') {
      setViewMode('table');
    }
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddOptions && !event.target.closest('.header-actions')) {
        setShowAddOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddOptions]);

  const loadProducts = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const productsData = await getDocuments(userId, 'products');
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeScanned = async (code) => {
    // Evitar procesamiento múltiple
    if (isProcessingBarcode) {
      console.log('Ya se está procesando un código, ignorando...');
      return;
    }
    
    console.log('=== INICIO ESCANEO ===');
    console.log('Código escaneado:', code);
    console.log('Tipo de código:', typeof code);
    console.log('Longitud del código:', code.length);
    console.log('UserId:', userId);
    
    setIsProcessingBarcode(true);
    setIsManualEntry(false);
    
    // Cerrar el escáner inmediatamente
    setShowScanner(false);
    
    try {
      // Primero, listar TODOS los productos para debug
      console.log('Listando todos los productos para debug...');
      const allProducts = await getDocuments(userId, 'products');
      console.log('Total de productos en inventario:', allProducts.length);
      allProducts.forEach(p => {
        console.log(`Producto: ${p.name}, Código: "${p.barcode}", Tipo: ${typeof p.barcode}`);
      });
      
      // Buscar en el inventario del usuario
      console.log('Buscando en inventario del usuario...');
      let existingProducts = await getDocuments(userId, 'products', [
        { field: 'barcode', operator: '==', value: code }
      ]);
      
      // Si no encuentra, intentar con codigoBarras
      if (existingProducts.length === 0) {
        console.log('No encontrado con "barcode", intentando con "codigoBarras"...');
        existingProducts = await getDocuments(userId, 'products', [
          { field: 'codigoBarras', operator: '==', value: code }
        ]);
      }
      
      console.log('Productos existentes encontrados:', existingProducts.length);
      console.log('Productos:', existingProducts);
    
    if (existingProducts.length > 0) {
      const existingProduct = existingProducts[0];
      console.log('Producto existente encontrado:', existingProduct);
      
      setExistingProductFound(existingProduct);
      
      // Manejar ambos formatos de datos (barcode/codigoBarras, name/nombre, etc.)
      setFormData({
        barcode: existingProduct.barcode || existingProduct.codigoBarras || code,
        name: existingProduct.name || existingProduct.nombre || '',
        laboratory: existingProduct.laboratory || existingProduct.proveedor || '',
        description: existingProduct.description || existingProduct.sustancia || '',
        cost: formatInputNumber(existingProduct.cost?.toString() || existingProduct.precios?.real?.toString() || ''),
        salePrice: formatInputNumber(existingProduct.salePrice?.toString() || existingProduct.precios?.corriente?.toString() || ''),
        stock: '1', // Nueva cantidad a agregar
        invoiceNumber: existingProduct.invoiceNumber || '',
        entryDate: format(new Date(), 'yyyy-MM-dd'),
        expirationDate: existingProduct.expirationDate ? 
          format(new Date(existingProduct.expirationDate), 'yyyy-MM-dd') : 
          format(new Date(new Date().setFullYear(new Date().getFullYear() + 1)), 'yyyy-MM-dd')
      });
    } else {
      // Si no existe en el inventario, buscar primero en medicamentos
      console.log('Buscando en colección medicamentos...');
      let medicamentoProduct = null;
      
      try {
        medicamentoProduct = await buscarMedicamentoPorCodigo(code);
        console.log('Producto de medicamentos:', medicamentoProduct);
      } catch (medicamentoError) {
        console.log('Error buscando en medicamentos:', medicamentoError);
      }
      
      if (medicamentoProduct) {
        setExistingProductFound(null);
        setFormData(prev => ({
          ...prev,
          barcode: code,
          name: medicamentoProduct.nombre || '',
          laboratory: medicamentoProduct.proveedor || '',
          description: `${medicamentoProduct.sustancia} - ${medicamentoProduct.presentacion}` || '',
          cost: formatInputNumber(medicamentoProduct.precios?.real?.toString() || medicamentoProduct.precios?.corriente?.toString() || ''),
          salePrice: formatInputNumber(medicamentoProduct.precios?.corriente?.toString() || ''),
          stock: '1',
          invoiceNumber: '',
          entryDate: format(new Date(), 'yyyy-MM-dd'),
          expirationDate: format(new Date(new Date().setFullYear(new Date().getFullYear() + 1)), 'yyyy-MM-dd')
        }));
      } else {
        // Si no está en medicamentos, buscar en el catálogo global
        console.log('No encontrado en medicamentos, buscando en catálogo global...');
        let catalogProduct = null;
        
        try {
          catalogProduct = await buscarProductoEnCatalogo(code);
          console.log('Producto del catálogo:', catalogProduct);
        } catch (catalogError) {
          console.log('No se pudo acceder al catálogo global, continuando...');
        }
        
        if (catalogProduct) {
          setExistingProductFound(null);
          setFormData(prev => ({
            ...prev,
            barcode: code,
            name: catalogProduct.nombre || '',
            laboratory: catalogProduct.proveedor || '',
            description: `${catalogProduct.sustancia} - ${catalogProduct.presentacion}` || '',
            cost: formatInputNumber(catalogProduct.precios?.real?.toString() || catalogProduct.precios?.corriente?.toString() || ''),
            salePrice: formatInputNumber(catalogProduct.precios?.corriente?.toString() || ''),
            stock: '1',
            invoiceNumber: '',
            entryDate: format(new Date(), 'yyyy-MM-dd'),
            expirationDate: format(new Date(new Date().setFullYear(new Date().getFullYear() + 1)), 'yyyy-MM-dd')
          }));
        } else {
          // Si no está en ningún lado, buscar en productos predefinidos locales
          console.log('No encontrado en catálogo, buscando en predefinidos...');
          const predefined = predefinedProducts.find(p => p.code === code);
          console.log('Producto predefinido:', predefined);
          
          setExistingProductFound(null);
          setFormData({
            barcode: code,
            name: predefined?.name || '',
            laboratory: predefined?.laboratory || '',
            description: predefined?.description || '',
            cost: formatInputNumber(predefined?.cost?.toString() || ''),
            salePrice: formatInputNumber(predefined?.salePrice?.toString() || ''),
            stock: '1',
            invoiceNumber: '',
            entryDate: format(new Date(), 'yyyy-MM-dd'),
            expirationDate: format(new Date(new Date().setFullYear(new Date().getFullYear() + 1)), 'yyyy-MM-dd')
          });
        }
      }
    }
    
    console.log('Abriendo modal de agregar producto...');
    
    // Usar setTimeout para asegurar que el estado se actualice antes de abrir el modal
    setTimeout(() => {
      setShowAddModal(true);
      setIsProcessingBarcode(false); // Reset el flag
      console.log('Modal abierto');
      console.log('=== FIN ESCANEO ===');
    }, 100);
    
    } catch (error) {
      console.error('Error en handleBarcodeScanned:', error);
      alert('Error al buscar el producto: ' + error.message);
      setIsProcessingBarcode(false); // Reset el flag en caso de error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        cost: parseFormattedNumber(formData.cost),
        salePrice: parseFormattedNumber(formData.salePrice),
        stock: parseInt(formData.stock),
        entryDate: new Date(formData.entryDate).toISOString(),
        expirationDate: new Date(formData.expirationDate).toISOString()
      };

      if (editingProduct) {
        await updateDocument(userId, 'products', editingProduct.id, productData);
      } else {
        // Verificar si el producto ya existe por código de barras
        const existingProducts = await getDocuments(userId, 'products', [
          { field: 'barcode', operator: '==', value: formData.barcode }
        ]);

        if (existingProducts.length > 0) {
          // Si existe, actualizar el stock sumando la cantidad nueva
          const existingProduct = existingProducts[0];
          const updatedStock = existingProduct.stock + parseInt(formData.stock);
          
          await updateDocument(userId, 'products', existingProduct.id, {
            ...existingProduct,
            stock: updatedStock,
            // Actualizar el costo y precio de venta con los nuevos valores si es necesario
            cost: parseFormattedNumber(formData.cost),
            salePrice: parseFormattedNumber(formData.salePrice),
            // Mantener la fecha de vencimiento más próxima
            expirationDate: new Date(formData.expirationDate) < new Date(existingProduct.expirationDate) 
              ? new Date(formData.expirationDate).toISOString() 
              : existingProduct.expirationDate,
            updatedAt: new Date().toISOString()
          });
          
          alert(`Stock actualizado: ${existingProduct.name} ahora tiene ${updatedStock} unidades`);
        } else {
          // Si no existe, crear nuevo producto
          await addDocument(userId, 'products', productData);
        }
      }

      await loadProducts();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error al guardar el producto');
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('¿Está seguro de eliminar este producto?')) {
      try {
        await deleteDocument(userId, 'products', productId);
        await loadProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Error al eliminar el producto');
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      barcode: product.barcode,
      name: product.name,
      laboratory: product.laboratory,
      description: product.description || '',
      invoiceNumber: product.invoiceNumber,
      entryDate: format(new Date(product.entryDate), 'yyyy-MM-dd'),
      expirationDate: format(new Date(product.expirationDate), 'yyyy-MM-dd'),
      cost: formatInputNumber(product.cost.toString()),
      salePrice: formatInputNumber(product.salePrice.toString()),
      stock: product.stock.toString()
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingProduct(null);
    setExistingProductFound(null);
    setIsManualEntry(false);
    setFormData({
      barcode: '',
      name: '',
      laboratory: '',
      description: '',
      invoiceNumber: '',
      entryDate: format(new Date(), 'yyyy-MM-dd'),
      expirationDate: '',
      cost: '',
      salePrice: '',
      stock: '1'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle formatted number inputs for cost and salePrice
    if (name === 'cost' || name === 'salePrice') {
      const formattedValue = formatInputNumber(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const filteredProducts = products
    .filter(product => {
      // Filtro de búsqueda
      const matchesSearch = 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.barcode.includes(searchTerm) ||
        product.laboratory.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro de estado
      if (filterStatus === 'all') return matchesSearch;
      
      const today = new Date();
      const expirationDate = new Date(product.expirationDate);
      const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
      
      if (filterStatus === 'expired') {
        return matchesSearch && daysUntilExpiration < 0;
      } else if (filterStatus === 'expiring') {
        return matchesSearch && daysUntilExpiration >= 0 && daysUntilExpiration <= 30;
      } else if (filterStatus === 'low-stock') {
        return matchesSearch && product.stock < 10;
      }
      
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'expiration':
          return new Date(a.expirationDate) - new Date(b.expirationDate);
        case 'stock':
          return a.stock - b.stock;
        case 'laboratory':
          return a.laboratory.localeCompare(b.laboratory);
        default:
          return 0;
      }
    });

  const isExpired = (date) => {
    return new Date(date) < new Date();
  };

  const isExpiringSoon = (date) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(date) <= thirtyDaysFromNow && !isExpired(date);
  };

  // Calcular estadísticas del inventario
  const calculateInventoryStats = () => {
    const stats = {
      totalProducts: products.length,
      totalItems: 0,
      totalInvestment: 0,
      totalValue: 0,
      lowStockCount: 0,
      expiredCount: 0,
      expiringSoonCount: 0
    };

    products.forEach(product => {
      const stock = product.stock || 0;
      const cost = product.cost || 0;
      const salePrice = product.salePrice || 0;
      
      stats.totalItems += stock;
      stats.totalInvestment += stock * cost;
      stats.totalValue += stock * salePrice;
      
      if (stock < 10) {
        stats.lowStockCount++;
      }
      
      if (isExpired(product.expirationDate)) {
        stats.expiredCount++;
      } else if (isExpiringSoon(product.expirationDate)) {
        stats.expiringSoonCount++;
      }
    });

    return stats;
  };

  const stats = calculateInventoryStats();

  return (
    <PageContainer>
      <Header>
        <h1>Inventario</h1>
        <HeaderActions className="header-actions">
          <Button onClick={() => setShowAddOptions(!showAddOptions)}>
            Agregar Producto
          </Button>
          {showAddOptions && (
            <AddOptionsDropdown>
              <AddOption onClick={() => {
                setShowAddOptions(false);
                setIsProcessingBarcode(false); // Reset el flag al abrir el escáner
                setShowScanner(true);
              }}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM9 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM9 10a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3zM15 4a1 1 0 011-1h1a1 1 0 011 1v1a1 1 0 01-1 1h-1a1 1 0 01-1-1V4zM15 8a1 1 0 011-1h1a1 1 0 011 1v1a1 1 0 01-1 1h-1a1 1 0 01-1-1V8zM15 12a1 1 0 011-1h1a1 1 0 011 1v1a1 1 0 01-1 1h-1a1 1 0 01-1-1v-1z" />
                </svg>
                <span>Escanear código</span>
              </AddOption>
              <AddOption onClick={() => {
                setShowAddOptions(false);
                setIsManualEntry(true);
                setShowAddModal(true);
              }}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V8z" clipRule="evenodd" />
                </svg>
                <span>Agregar manual</span>
              </AddOption>
            </AddOptionsDropdown>
          )}
        </HeaderActions>
      </Header>

      <StatsContainer>
        <StatCard
          $accent="linear-gradient(180deg, #60A5FA 0%, #2563EB 100%)"
          $glow="radial-gradient(circle, rgba(37,99,235,0.12), transparent 65%)"
        >
          <StatLabel>Valor Total Invertido</StatLabel>
          <StatValue $gradient="linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)">
            ${stats.totalInvestment.toLocaleString('es-CO')}
          </StatValue>
          <StatSubtext>{stats.totalItems} productos en stock</StatSubtext>
        </StatCard>

        <StatCard
          $accent="linear-gradient(180deg, #34D399 0%, #059669 100%)"
          $glow="radial-gradient(circle, rgba(5,150,105,0.12), transparent 65%)"
        >
          <StatLabel>Valor Potencial de Venta</StatLabel>
          <StatValue $gradient="linear-gradient(135deg, #059669 0%, #047857 100%)">
            ${stats.totalValue.toLocaleString('es-CO')}
          </StatValue>
          <StatSubtext>Ganancia potencial: ${(stats.totalValue - stats.totalInvestment).toLocaleString('es-CO')}</StatSubtext>
        </StatCard>

        <StatCard
          $accent="linear-gradient(180deg, #FCD34D 0%, #D97706 100%)"
          $glow="radial-gradient(circle, rgba(217,119,6,0.14), transparent 65%)"
        >
          <StatLabel>Productos Totales</StatLabel>
          <StatValue $gradient="linear-gradient(135deg, #D97706 0%, #B45309 100%)">
            {stats.totalProducts}
          </StatValue>
          <StatSubtext>{stats.lowStockCount} con stock bajo</StatSubtext>
        </StatCard>

        <StatCard
          $accent="linear-gradient(180deg, #FB7185 0%, #E11D48 100%)"
          $glow="radial-gradient(circle, rgba(225,29,72,0.14), transparent 65%)"
        >
          <StatLabel>Alertas</StatLabel>
          <StatValue $gradient="linear-gradient(135deg, #E11D48 0%, #BE123C 100%)">
            {stats.expiredCount + stats.expiringSoonCount}
          </StatValue>
          <StatSubtext>{stats.expiredCount} vencidos, {stats.expiringSoonCount} por vencer</StatSubtext>
        </StatCard>
      </StatsContainer>

      <div style={{ marginBottom: '1rem' }}>
        <ViewToggle>
          <Button 
            variant={viewMode === 'grid' ? 'primary' : 'secondary'}
            onClick={() => setViewMode('grid')}
            size="small"
          >
            Vista Tarjetas
          </Button>
          <Button 
            variant={viewMode === 'table' ? 'primary' : 'secondary'}
            onClick={() => setViewMode('table')}
            size="small"
          >
            Vista Tabla
          </Button>
        </ViewToggle>
      </div>

      {viewMode === 'table' && (
        <FilterContainer>
          <FilterGroup>
            <label>Buscar:</label>
            <Input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', maxWidth: '300px' }}
            />
          </FilterGroup>
          
          <FilterGroup>
            <label>Estado:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">Todos</option>
              <option value="expired">Vencidos</option>
              <option value="expiring">Por vencer (30 días)</option>
              <option value="low-stock">Stock bajo (&lt; 10)</option>
            </select>
          </FilterGroup>
          
          <FilterGroup>
            <label>Ordenar por:</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="name">Nombre</option>
              <option value="expiration">Fecha de vencimiento</option>
              <option value="stock">Stock</option>
              <option value="laboratory">Laboratorio</option>
            </select>
          </FilterGroup>
        </FilterContainer>
      )}

      {viewMode === 'grid' && (
        <SearchBar>
          <Input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, marginBottom: 0, fontSize: '14px' }}
          />
        </SearchBar>
      )}

      {loading ? (
        <p>Cargando productos...</p>
      ) : viewMode === 'grid' ? (
        <ProductGrid>
          {filteredProducts.map(product => (
            <ProductCard 
              key={product.id}
              className={
                isExpired(product.expirationDate) ? 'expired' : 
                isExpiringSoon(product.expirationDate) ? 'expiring-soon' : ''
              }
            >
              {isExpired(product.expirationDate) && (
                <ExpirationBadge expired>Vencido</ExpirationBadge>
              )}
              {isExpiringSoon(product.expirationDate) && (
                <ExpirationBadge>Próximo a vencer</ExpirationBadge>
              )}
              
              <h3>{product.name}</h3>

              <ProductInfoGrid>
                <ProductInfo>
                  <div className="icon"><Barcode /></div>
                  <div className="content">
                    <label>Código</label>
                    <p>{product.barcode || '—'}</p>
                  </div>
                </ProductInfo>

                <ProductInfo>
                  <div className="icon"><Building2 /></div>
                  <div className="content">
                    <label>Laboratorio</label>
                    <p>{product.laboratory || '—'}</p>
                  </div>
                </ProductInfo>

                <ProductInfo>
                  <div className="icon"><Package /></div>
                  <div className="content">
                    <label>Stock</label>
                    <p>
                      <StockBadge $low={product.stock < 10}>
                        {product.stock} u.
                      </StockBadge>
                    </p>
                  </div>
                </ProductInfo>

                <ProductInfo>
                  <div className="icon"><Calendar /></div>
                  <div className="content">
                    <label>Vence</label>
                    <p>{format(new Date(product.expirationDate), 'dd/MM/yyyy', { locale: es })}</p>
                  </div>
                </ProductInfo>
              </ProductInfoGrid>

              <PriceInfo>
                <div className="cost">
                  <label>Costo</label>
                  <div className="value">{formatCurrency(product.cost)}</div>
                </div>
                <div className="sale">
                  <label>P. Venta</label>
                  <div className="value">{formatCurrency(product.salePrice)}</div>
                </div>
              </PriceInfo>

              <ActionButtons>
                <Button size="small" onClick={() => handleEdit(product)}>
                  <Edit3 size={14} style={{ marginRight: 4 }} />
                  Editar
                </Button>
                <Button size="small" variant="danger" onClick={() => handleDelete(product.id)}>
                  <Trash2 size={14} style={{ marginRight: 4 }} />
                  Eliminar
                </Button>
              </ActionButtons>
            </ProductCard>
          ))}
        </ProductGrid>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <Th style={window.innerWidth <= 768 ? {width: '15%'} : {}}>Código</Th>
                <Th style={window.innerWidth <= 768 ? {width: '20%'} : {}}>Nombre</Th>
                <Th style={window.innerWidth <= 768 ? {width: '15%'} : {}}>Lab.</Th>
                <Th style={window.innerWidth <= 768 ? {width: '8%'} : {}}>Stock</Th>
                <Th style={window.innerWidth <= 768 ? {display: 'none'} : {}}>Costo</Th>
                <Th style={window.innerWidth <= 768 ? {width: '12%'} : {}}>P.Venta</Th>
                <Th style={window.innerWidth <= 768 ? {display: 'none'} : {}}>Factura</Th>
                <Th style={window.innerWidth <= 768 ? {display: 'none'} : {}}>Fecha Compra</Th>
                <Th style={window.innerWidth <= 768 ? {width: '15%'} : {}}>Venc.</Th>
                <Th style={window.innerWidth <= 768 ? {width: '15%'} : {}}>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => {
                const expirationDate = new Date(product.expirationDate);
                const today = new Date();
                const daysUntilExpiration = Math.ceil((expirationDate - today) / (1000 * 60 * 60 * 24));
                const isExpiredProduct = daysUntilExpiration < 0;
                const isExpiringSoonProduct = daysUntilExpiration >= 0 && daysUntilExpiration <= 30;
                
                return (
                  <Tr key={product.id}>
                    <Td>{product.barcode}</Td>
                    <Td title={product.name}>{product.name}</Td>
                    <Td title={product.laboratory}>{product.laboratory}</Td>
                    <Td style={{ fontWeight: product.stock < 10 ? 'bold' : 'normal', color: product.stock < 10 ? '#F39C12' : 'inherit' }}>
                      {product.stock}
                    </Td>
                    <Td style={window.innerWidth <= 768 ? {display: 'none'} : {}}>{formatCurrency(product.cost)}</Td>
                    <Td style={{ fontWeight: 'bold', color: '#27AE60' }}>${product.salePrice}</Td>
                    <Td style={window.innerWidth <= 768 ? {display: 'none'} : {}}>{product.invoiceNumber || '-'}</Td>
                    <Td style={window.innerWidth <= 768 ? {display: 'none'} : {}}>{format(new Date(product.entryDate), 'dd/MM/yyyy', { locale: es })}</Td>
                    <Td className={isExpiredProduct ? 'expired' : isExpiringSoonProduct ? 'expiring' : ''}>
                      {window.innerWidth <= 768 
                        ? format(expirationDate, 'dd/MM', { locale: es })
                        : format(expirationDate, 'dd/MM/yyyy', { locale: es })
                      }
                      {window.innerWidth <= 768 && isExpiredProduct && '!'}
                    </Td>
                    <Td>
                      <ActionButtons>
                        <Button size="small" onClick={() => handleEdit(product)}>
                          Editar
                        </Button>
                        <Button size="small" variant="danger" onClick={() => handleDelete(product.id)}>
                          Eliminar
                        </Button>
                      </ActionButtons>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}

      <ZXingScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScanned}
      />

      <Modal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        title={editingProduct ? 'Editar Producto' : 'Agregar Producto'}
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          {existingProductFound && !editingProduct && (
            <div style={{ 
              backgroundColor: '#E8F5E9', 
              border: '1px solid #4CAF50', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '16px' 
            }}>
              <h4 style={{ color: '#2E7D32', marginBottom: '8px' }}>
                ¡Producto existente encontrado!
              </h4>
              <p style={{ color: '#2E7D32', marginBottom: '8px' }}>
                Este producto ya existe en tu inventario con {existingProductFound.stock} unidades.
              </p>
              <p style={{ color: '#2E7D32', fontSize: '14px' }}>
                Al guardar, se sumarán las cantidades al stock existente.
              </p>
            </div>
          )}
          <Input
            label="Código de Barras"
            name="barcode"
            value={formData.barcode}
            onChange={handleInputChange}
            required
            disabled={editingProduct}
          />
          
          {isManualEntry ? (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Nombre del Producto *
              </label>
              <AutocompleteInput
                value={formData.name}
                onChange={(value) => setFormData(prev => ({ ...prev, name: value }))}
                onSelect={(medicamento) => {
                  // Al seleccionar un medicamento, autocompletar todos los campos
                  setFormData(prev => ({
                    ...prev,
                    name: medicamento.nombre || '',
                    laboratory: medicamento.proveedor || prev.laboratory,
                    description: medicamento.sustancia ? 
                      `${medicamento.sustancia}${medicamento.presentacion ? ' - ' + medicamento.presentacion : ''}` : 
                      prev.description,
                    barcode: medicamento.codigoBarras || medicamento.id || prev.barcode,
                    cost: formatInputNumber(medicamento.precios?.real?.toString() || medicamento.precios?.corriente?.toString() || prev.cost),
                    salePrice: formatInputNumber(medicamento.precios?.corriente?.toString() || prev.salePrice)
                  }));
                }}
                placeholder="Escribe el nombre del medicamento..."
                required
              />
            </div>
          ) : (
            <Input
              label="Nombre del Producto"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          )}
          
          <Input
            label="Laboratorio"
            name="laboratory"
            value={formData.laboratory}
            onChange={handleInputChange}
            required
          />
          
          <Input
            label="Descripción"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
          />
          
          <Input
            label="Número de Factura"
            name="invoiceNumber"
            value={formData.invoiceNumber}
            onChange={handleInputChange}
            required
          />
          
          <Input
            label="Fecha de Ingreso"
            name="entryDate"
            type="date"
            value={formData.entryDate}
            onChange={handleInputChange}
            required
          />
          
          <Input
            label="Fecha de Vencimiento"
            name="expirationDate"
            type="date"
            value={formData.expirationDate}
            onChange={handleInputChange}
            required
          />
          
          <Input
            label="Costo"
            name="cost"
            type="text"
            value={formData.cost}
            onChange={handleInputChange}
            placeholder="0"
            required
          />
          
          <Input
            label="Precio de Venta"
            name="salePrice"
            type="text"
            value={formData.salePrice}
            onChange={handleInputChange}
            placeholder="0"
            required
          />
          
          <Input
            label={existingProductFound && !editingProduct ? "Cantidad a Agregar" : "Stock Inicial"}
            name="stock"
            type="number"
            value={formData.stock}
            onChange={handleInputChange}
            required
          />
          
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit">
              {editingProduct ? 'Actualizar' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>
    </PageContainer>
  );
};